import { useCallback } from 'react';

import { produce } from 'immer';

import { useHubTranslationsContext } from './hub-translations-context';

type UseBlockTranslationsReturn = {
	ensureBlockEntry: (translationLocale: string) => void;
	removeLocale: (translationLocale: string) => void;
	setFieldValue: (translationLocale: string, concreteFieldPath: string, value: string) => void;
	stampExtensionType: (localesToStamp: string[]) => void;
};

/**
 * A focused hook that encapsulates all `storedTranslations` mutations for a
 * single block (identified by `localId`).  Callers get a clean API instead of
 * repeated manual spread-and-merge patterns; immer's `produce` keeps each
 * mutation readable and avoids accidental reference sharing.
 */
export const useBlockTranslations = ({
	localId,
	extensionType,
}: {
	extensionType: string;
	localId: string;
}): UseBlockTranslationsReturn => {
	const hubTranslationsCtx = useHubTranslationsContext();
	const setStoredTranslations = hubTranslationsCtx?.setStoredTranslations;
	const markLocaleDirty = hubTranslationsCtx?.markLocaleDirty;

	/**
	 * Stamp `extensionType` onto every locale entry that already has a block for
	 * this `localId`.  Called once on mount to keep the stored metadata in sync
	 * with the current prop value.
	 */
	const stampExtensionType = useCallback(
		(localesToStamp: string[]) => {
			if (localesToStamp.length === 0) {
				return;
			}
			setStoredTranslations?.(
				produce((draft) => {
					for (const loc of localesToStamp) {
						const block = draft[loc]?.[localId];
						if (block) {
							block.extensionType = extensionType;
						}
					}
				}),
			);
		},
		[setStoredTranslations, localId, extensionType],
	);

	/**
	 * Ensure an (initially empty) block entry exists for a newly added locale so
	 * the form renders empty fields immediately.  Does nothing when stored data
	 * for this block already exists.
	 */
	const ensureBlockEntry = useCallback(
		(translationLocale: string) => {
			setStoredTranslations?.(
				produce((draft) => {
					if (!draft[translationLocale]) {
						draft[translationLocale] = {};
					}
					if (!draft[translationLocale][localId]) {
						draft[translationLocale][localId] = { extensionType, fields: {} };
					}
				}),
			);
		},
		[setStoredTranslations, localId, extensionType],
	);

	/**
	 * Update a single translated field value.
	 * Seeds `extensionType` and an empty `fields` map when the block is new.
	 */
	const setFieldValue = useCallback(
		(translationLocale: string, concreteFieldPath: string, value: string) => {
			setStoredTranslations?.(
				produce((draft) => {
					if (!draft[translationLocale]) {
						draft[translationLocale] = {};
					}
					if (!draft[translationLocale][localId]) {
						draft[translationLocale][localId] = { extensionType, fields: {} };
					}
					draft[translationLocale][localId].fields[concreteFieldPath] = value;
				}),
			);
			markLocaleDirty?.(translationLocale);
		},
		[setStoredTranslations, markLocaleDirty, localId, extensionType],
	);

	/**
	 * Remove all stored data for this block under the given locale.
	 * If that empties the locale bucket entirely, the locale key is removed too.
	 */
	const removeLocale = useCallback(
		(translationLocale: string) => {
			setStoredTranslations?.(
				produce((draft) => {
					const localeData = draft[translationLocale];
					if (!localeData) {
						return;
					}
					delete localeData[localId];
					if (Object.keys(localeData).length === 0) {
						delete draft[translationLocale];
					}
				}),
			);
		},
		[setStoredTranslations, localId],
	);

	return { stampExtensionType, ensureBlockEntry, setFieldValue, removeLocale };
};
